import { v4 as uuidv4 } from "uuid"

import type { ComponentTypeExtractor } from "../features/ImportFlow/Interfaces"
import type { GroupConditions, RuleCardFormikValues } from "../features/Ruleset"
import type { ScoreCardFormikValues } from "../features/Scorecards/Interfaces"
import type { TagCardFormikValues } from "../features/Taglist"
import type { SchemaFeature } from "../features/Workflows"
import { Operators } from "../types/custom/projects"
import type { Rule } from "../types/custom/rules"
import { MapValueTypeToFeatureType } from "../types/custom/rules"
import { multiValuesOperators } from "./conditionHelpers"
import { convertRuleGroupConditionsToComplexCondition } from "./rulesetHelpers"

interface FormatComponentParams {
  name: string
  cards: RuleCardFormikValues["cards"] | TagCardFormikValues["cards"] | ScoreCardFormikValues["cards"]
}

interface FormatComponentResult {
  trimmedName: string
  finalComponentFormat: ComponentTypeExtractor<"Ruleset" | "Taglist" | "Scorecard", "formattedCardRequest">[]
  featuresArray: Array<SchemaFeature>
}

export function formatWorkflowComponent({ name, cards }: FormatComponentParams): FormatComponentResult {
  if (!name || !cards || !Array.isArray(cards)) {
    return {
      trimmedName: "",
      finalComponentFormat: [],
      featuresArray: [],
    }
  }

  const finalComponentFormat: ComponentTypeExtractor<"Ruleset" | "Taglist" | "Scorecard", "formattedCardRequest">[] = []
  const featuresArray: Array<SchemaFeature> = []
  const trimmedName = name.trim()

  for (const card of cards) {
    const trimmedComponentName = card?.name?.trim()

    // helpers to collect features from any depth
    const collectFromConditions = (conditions: Rule[] | undefined): void => {
      if (!conditions || conditions.length === 0) return
      conditions.forEach((item: Rule) => {
        if (featuresArray.every((el) => el.name?.toLowerCase() !== item.feature?.toLowerCase())) {
          featuresArray.push({
            name: item?.feature,
            is_required: true,
            source: "workflow",
            type: MapValueTypeToFeatureType[item?.type as keyof typeof MapValueTypeToFeatureType],
          })
        }
        if (multiValuesOperators?.includes(Operators[item.operator as keyof typeof Operators])) {
          item.type = item.operator
        }
      })
    }

    const collectFromGroup = (group: GroupConditions | undefined): void => {
      if (!group) return
      collectFromConditions(group.conditions)
      if (group.groups && group.groups.length > 0) {
        group.groups.forEach((g) => collectFromGroup(g))
      }
    }

    collectFromConditions(card.conditions as Rule[])
    if (card.groups && card.groups.length > 0) {
      card.groups.forEach((g: GroupConditions) => collectFromGroup(g))
    }

    let newRule: { name: string; condition: string; return_label?: string; return_value?: number } = {
      name: "",
      condition: "",
    }

    const safeRuleName =
      trimmedComponentName && trimmedComponentName.length > 0 ? trimmedComponentName : `rule-${uuidv4()}`

    newRule = {
      name: safeRuleName,
      condition: convertRuleGroupConditionsToComplexCondition(card) ?? "",
    }

    if ("label_id" in card && typeof card.label_id === "string") {
      newRule["return_label"] = card.label_id
    }

    if (
      "return_value" in card &&
      card.return_value !== undefined &&
      card.return_value !== null &&
      `${card.return_value}`.trim() !== "" &&
      !isNaN(Number(card.return_value))
    ) {
      newRule["return_value"] = Number(card.return_value)
    }

    finalComponentFormat.push(newRule)
  }

  return { trimmedName, featuresArray, finalComponentFormat: finalComponentFormat }
}
